/**
 * Google Maps Response Transformer
 * Converts Google Maps API responses to standardized format
 */

/**
 * Transform Google Directions API response to standard format
 * @param {Object} googleResponse - Raw Google Directions API response
 * @returns {Object} Standardized route object
 */
export function transformDirectionsResponse(googleResponse) {
  if (
    !googleResponse ||
    googleResponse.status !== 'OK' ||
    !googleResponse.routes ||
    !googleResponse.routes[0]
  ) {
    return null;
  }

  const route = googleResponse.routes[0];
  const leg = route.legs[0];

  return {
    geometry: {
      type: 'LineString',
      coordinates: decodePolyline(route.overview_polyline.points).map((point) => [
        point.lng,
        point.lat,
      ]),
    },
    distance: leg.distance.value / 1000, // Convert meters to km
    duration: leg.duration.value / 60, // Convert seconds to minutes
    distanceText: leg.distance.text,
    durationText: leg.duration.text,
    startAddress: leg.start_address,
    endAddress: leg.end_address,
    steps: leg.steps.map((step) => ({
      distance: step.distance.value,
      duration: step.duration.value,
      instruction: step.html_instructions.replace(/<[^>]*>/g, ''), // Strip HTML
      maneuver: step.maneuver,
      polyline: step.polyline.points,
    })),
    bounds: route.bounds,
    warnings: route.warnings || [],
  };
}

/**
 * Transform Google Geocoding API response to standard format
 * @param {Object} googleResponse - Raw Google Geocoding API response
 * @returns {Object|null} Standardized geocoding result
 */
export function transformGeocodingResponse(googleResponse) {
  if (
    !googleResponse ||
    googleResponse.status !== 'OK' ||
    !googleResponse.results ||
    !googleResponse.results[0]
  ) {
    return null;
  }

  const result = googleResponse.results[0];

  return {
    formattedAddress: result.formatted_address,
    location: {
      lat: result.geometry.location.lat,
      lng: result.geometry.location.lng,
    },
    placeId: result.place_id,
    types: result.types,
    addressComponents:
      result.address_components?.map((component) => ({
        longName: component.long_name,
        shortName: component.short_name,
        types: component.types,
      })) || [],
  };
}

/**
 * Transform Google Places API response to standard format
 * @param {Object} googleResponse - Raw Google Places API response
 * @returns {Array} Array of standardized place results
 */
export function transformPlacesResponse(googleResponse) {
  if (!googleResponse || googleResponse.status !== 'OK' || !googleResponse.results) {
    return [];
  }

  return googleResponse.results.map((place) => ({
    placeId: place.place_id,
    name: place.name,
    formattedAddress: place.formatted_address,
    location: {
      lat: place.geometry.location.lat,
      lng: place.geometry.location.lng,
    },
    types: place.types,
    rating: place.rating,
    userRatingsTotal: place.user_ratings_total,
  }));
}

/**
 * Decode Google polyline to array of coordinates
 * @param {string} encoded - Encoded polyline string
 * @returns {Array<{lat: number, lng: number}>}
 */
export function decodePolyline(encoded) {
  const coordinates = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    coordinates.push({
      lat: lat / 1e5,
      lng: lng / 1e5,
    });
  }

  return coordinates;
}

/**
 * Transform route data to GeoJSON format for map rendering
 * @param {Object} routeData - Standardized route data
 * @returns {Object} GeoJSON feature
 */
export function toGeoJSON(routeData) {
  if (!routeData || !routeData.geometry) {
    return null;
  }

  return {
    type: 'Feature',
    geometry: routeData.geometry,
    properties: {
      distance: routeData.distance,
      duration: routeData.duration,
      distanceText: routeData.distanceText,
      durationText: routeData.durationText,
    },
  };
}
